import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatPlatformName, getIcon } from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import LoadingComponent from "../../components/common/LoadingComponent";
import { getAllIntegration } from "../../features/meeting-integration/meetingIntegrationSlice";
import {
  fetchIntegrationSettingsByUserId,
  upsertIntegrationSetting,
} from "../../features/integration-setting/integrationSettingSlice";
import { createAuthState, getIntegrationAuthUrl } from "./Integrations.utils";
import { useNavigate } from "react-router-dom";
import { requireSetup } from "./Integrations.utils";

function Integrations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState("");

  const { integrationSettings, loading: settingLoading } = useSelector(
    (state) => state.integrationSetting,
  );

  const { integrations = [], loading: integrationsLoading } = useSelector(
    (state) => state.meetingIntegrations,
  );
  const installedSettings = integrationSettings || [];
  const availableIntegrations = integrations || [];

  useEffect(() => {
    const timeout = setTimeout(
      () => {
        dispatch(fetchIntegrationSettingsByUserId(searchTerm));
        dispatch(getAllIntegration(searchTerm));
      },
      searchTerm.trim() ? 1000 : 0,
    );

    return () => clearTimeout(timeout);
  }, [searchTerm, dispatch]);

  const loading =
    (!installedSettings.length && settingLoading) ||
    (!availableIntegrations.length && integrationsLoading);

  const handleInstall = (integration) => {
    if (!integration?.isOauthRequired) {
      navigate(`/setup/${integration.uuid}`);
      return;
    }

    const state = createAuthState(user, integration);
    const url = getIntegrationAuthUrl(
      integration.uuid,
      integration.name,
      state,
    );
    window.location.href = url;
  };

  const handleSave = (setting) => {
    dispatch(
      upsertIntegrationSetting({
        uuid: setting.uuid,
        integrationName: editedName,
      }),
    );
    setEditingId(null);
  };

  return (
    <div className="container my-11">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Integrations</h2>

        <div className="w-50 position-relative">
          <input
            type="text"
            className="form-control ps-5"
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <i
            className={`wb ${Icons("search")} fs-8 text-secondary position-absolute`}
            style={{
              top: "50%",
              left: "15px",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          ></i>
        </div>
      </div>

      {loading ? (
        <LoadingComponent loading={true} size={15} />
      ) : (
        <>
          <div className="mb-5">
            <h4 className="mb-4 text-muted">INSTALLED</h4>

            {installedSettings.length === 0 ? (
              <div className="d-flex flex-column align-items-center">
                <p className="text-center mb-3">
                  No active integrations found.
                </p>
              </div>
            ) : (
              <div className="row g-4">
                {installedSettings.map((setting) => {
                  const integration = setting.integrationId;
                  const integrationName = integration?.name?.toLowerCase();
                  let iconSrc = getIcon(integrationName);
                  const displayName =
                    setting.integrationName || integration.name;

                  const needsSetup = requireSetup(integrationName, setting);

                  return (
                    <div key={setting.uuid} className="col-md-3 col-lg-3">
                      <div className="card text-center shadow-sm h-100 position-relative">
                        {needsSetup ? (
                          <span
                            className="position-absolute badge text-success"
                            style={{
                              top: "10px",
                              left: "10px",
                              fontSize: "0.7rem",
                              zIndex: 1,
                              border: "1px solid #38ef7d",
                              background: "transparent",
                            }}
                          >
                            Installed
                          </span>
                        ) : (
                          <span
                            className="position-absolute badge text-setup"
                            style={{
                              top: "10px",
                              left: "10px",
                              fontSize: "0.7rem",
                              zIndex: 1,
                              border: "1px solid #f27b7b",
                              background: "transparent",
                            }}
                          >
                            Setup Required
                          </span>
                        )}
                        <button
                          className="btn btn-sm p-0 position-absolute d-flex align-items-center justify-content-center"
                          style={{
                            top: "10px",
                            right: "10px",
                            color:
                              "var(--phoenix-navbar-vertical-link-active-color)",
                            width: "22px",
                            height: "22px",
                            borderRadius: "4px",
                            background: "transparent",
                            border: "none",
                            zIndex: 2,
                          }}
                          title="Configuration"
                          onClick={() => navigate(`${setting.uuid}`)}
                        >
                          <i
                            className={`wb ${Icons("constraints")} fs-8 align-middle`}
                          ></i>
                        </button>

                        <div className="card-body d-flex flex-column align-items-center pt-5">
                          <div
                            className="rounded-circle bg-secondary d-flex justify-content-center align-items-center mb-3"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <img
                              src={iconSrc}
                              alt={integration.name}
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "contain",
                              }}
                            />
                          </div>

                          {editingId === setting.uuid ? (
                            <div className="d-flex align-items-center justify-content-center gap-2 mt-2">
                              <input
                                type="text"
                                value={editedName}
                                maxLength={20}
                                onChange={(e) => setEditedName(e.target.value)}
                                className="bg-transparent border-0 text-center"
                                style={{
                                  width: "100px",
                                  outline: "none",
                                  boxShadow: "none",
                                  fontSize: "16px",
                                }}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSave(setting);
                                  if (e.key === "Escape") setEditingId(null);
                                }}
                              />

                              <i
                                className={`wb ${Icons("check")} text-success`}
                                style={{ cursor: "pointer", fontSize: "18px" }}
                                onClick={() => handleSave(setting)}
                              />

                              <i
                                className={`wb ${Icons("cross")} text-danger`}
                                style={{ cursor: "pointer", fontSize: "18px" }}
                                onClick={() => setEditingId(null)}
                              />
                            </div>
                          ) : (
                            <h5 className="card-title mb-3 text-truncate">
                              {formatPlatformName(displayName)}
                            </h5>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <hr className="my-5" />

          <div>
            <h4 className="mb-4 text-muted">AVAILABLE</h4>

            {availableIntegrations.length === 0 ? (
              <p className="text-center">No integrations available.</p>
            ) : (
              <div className="row g-4">
                {availableIntegrations.map((item) => {
                  const integration = item.data;
                  const key = item.name.toLowerCase();
                  const iconSrc = getIcon(key);
                  const isComingSoon = item.status === "coming_soon";

                  return (
                    <div key={item.name} className="col-md-3 col-sm-6">
                      <div className="card text-center shadow-sm h-100 position-relative">
                        <div className="card-body d-flex flex-column align-items-center">
                          <div
                            className="rounded-circle bg-secondary d-flex justify-content-center align-items-center mb-3"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <img
                              src={iconSrc}
                              alt={item.name}
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "contain",
                              }}
                            />
                          </div>

                          <h5 className="card-title mb-3">
                            {formatPlatformName(item.name)}
                          </h5>

                          <button
                            className={`btn flex-grow-1 btn-primary`}
                            onClick={() =>
                              !isComingSoon && handleInstall(integration)
                            }
                          >
                            <i
                              className={`wb ${Icons(
                                isComingSoon ? "clock" : "install",
                              )} fs-8 me-1 align-middle`}
                            />
                            {isComingSoon ? "Coming Soon" : "Install"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Integrations;
